import { unwrapAuthUserPayload } from '@/utils/authUserPayload'
import { listAllRbacApiNames, listRbacGroupNames } from '@/constants/rbacPages'
import { RBAC } from '@/constants/rbacPermissions'
import {
  collectPermissionNamesFromFormGrants,
  getRegistrationFormGrantsFromUser
} from '@/utils/registrationFormGrants'

/** JSON 定义的 API 叶子名集合（guard 白名单） */
let cachedLeafNameSet = null
function getKnownLeafNameSet () {
  if (!cachedLeafNameSet) cachedLeafNameSet = new Set(listAllRbacApiNames())
  return cachedLeafNameSet
}

/** JSON 定义的分组名（module / page.name，GET /user merge 的祖先名应排除） */
let cachedGroupNameSet = null
function getKnownGroupNameSet () {
  if (!cachedGroupNameSet) cachedGroupNameSet = new Set(listRbacGroupNames())
  return cachedGroupNameSet
}

/**
 * 从用户对象提取全部 permission.name（含后端 merge 的分组名）
 * @param {Record<string, unknown>|null|undefined} user
 * @returns {string[]}
 */
export function getPermissionNamesFromUser (user) {
  const roles = user?.roles
  if (!Array.isArray(roles)) return []
  const names = []
  for (const role of roles) {
    for (const perm of role?.permissions || []) {
      const name = perm?.name != null ? String(perm.name).trim() : ''
      if (name) names.push(name)
    }
  }
  return [...new Set(names)]
}

/**
 * guard / 菜单 / v-permission 用：只保留 API 叶子名
 * - 在 listAllRbacApiNames 中 → 保留
 * - 是已知分组名（活动管理、会议日程 等 page 名）→ 丢弃
 * - 其它未知名 → 保留（后端新增叶子、JSON 未同步时仍可鉴权）
 */
export function getGuardPermissionNamesFromUser (user) {
  const raw = getPermissionNamesFromUser(user)
  const leafSet = getKnownLeafNameSet()
  const groupSet = getKnownGroupNameSet()
  return raw.filter((name) => leafSet.has(name) || !groupSet.has(name))
}

/**
 * 从用户对象提取 resource_grants ACL 叶子权限（GET /user · 无 X-Company-Id 时亦返回全量 grant）
 * @param {Record<string, unknown>|null|undefined} user
 * @returns {string[]}
 */
export function getResourceGrantPermissionNamesFromUser (user) {
  const raw = user?.resourceGrantPermissionNames ?? user?.resource_grant_permission_names
  if (!Array.isArray(raw)) return []
  return [...new Set(raw.map((n) => String(n).trim()).filter(Boolean))]
}

/**
 * Spatie roles.permissions ∪ resourceGrantPermissionNames（guard 叶子名）
 * @deprecated 菜单/守卫请用 getSessionPermissionNamesFromUser（公司空间不 merge grant）
 */
export function getMergedPermissionNamesFromUser (user) {
  const spatie = getGuardPermissionNamesFromUser(user)
  const grants = getResourceGrantPermissionNamesFromUser(user)
  if (!grants.length) return spatie
  return [...new Set([...spatie, ...grants])]
}

/**
 * 当前会话的有效权限：企业 RBAC 与按用户授予的资源 ACL 叶子合并。
 */
export function getSessionPermissionNamesFromUser (user) {
  const spatie = getGuardPermissionNamesFromUser(user)
  const grantNames = getResourceGrantPermissionNamesFromUser(user)
  const formGrantNames = collectPermissionNamesFromFormGrants(getRegistrationFormGrantsFromUser(user))
  return [...new Set([...spatie, ...grantNames, ...formGrantNames])]
}

/** 从 localStorage.user + 当前 workspaceScope 读取会话权限 */
export function getSessionPermissionNamesFromStorage () {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return []
    return getSessionPermissionNamesFromUser(JSON.parse(raw))
  } catch {
    return []
  }
}

/**
 * 归一化表单 effectivePermissions（GET /registrations/{id}）
 * @param {unknown} raw
 * @returns {string[]}
 */
export function normalizeEffectivePermissions (raw) {
  const arr = Array.isArray(raw)
    ? raw
    : (raw?.effectivePermissions ?? raw?.effective_permissions)
  if (!Array.isArray(arr)) return []
  return [...new Set(arr.map((n) => String(n).trim()).filter(Boolean))]
}

/**
 * 可见权限 = user(Spatie ∪ ACL) ∪ form.effectivePermissions
 * @param {{ user?: Record<string, unknown>|null, formEffectivePermissions?: string[]|null|undefined }} [options]
 * @returns {string[]}
 */
export function getEffectivePermissionNames (options = {}) {
  const { user, formEffectivePermissions } = options
  let names = user
    ? getSessionPermissionNamesFromUser(user)
    : getSessionPermissionNamesFromStorage()
  const formPerms = normalizeEffectivePermissions(formEffectivePermissions)
  if (formPerms.length) {
    names = [...new Set([...names, ...formPerms])]
  }
  return names
}

/** 从 localStorage.user 读取 guard 用叶子权限名（随工作区：个人 merge ACL，公司仅 Spatie） */
export function getPermissionNamesFromStorage () {
  return getSessionPermissionNamesFromStorage()
}

/** 从 GET /user 响应体提取 guard 用叶子权限名（随当前 workspaceScope） */
export function getPermissionNamesFromGetUserResponse (res) {
  return getSessionPermissionNamesFromUser(unwrapAuthUserPayload(res))
}

/** 报名管理菜单 / 列表路由：Spatie 或 ACL 任一命中即可 */
export const REGISTRATION_NAV_PERMISSIONS = [
  RBAC.REGISTRATION_LIST,
  RBAC.REGISTRATION_VIEW,
  RBAC.REGISTRATION_EDIT,
  RBAC.PROXY_ENTRY,
  RBAC.PROXY_SUBMISSION_LIST,
  RBAC.PROXY_SUBMISSION_EDIT,
  RBAC.SUBMISSION_LIST,
  RBAC.SUBMISSION_EDIT,
  RBAC.ORDER_LIST
]

/** 是否可见「报名管理」模块（侧栏 / 列表路由） */
export function hasRegistrationModuleAccess (permissionNames) {
  return hasAnyPermission(permissionNames, REGISTRATION_NAV_PERMISSIONS)
}

/**
 * 规范化权限要求：string | string[] → string[]
 * @param {string|string[]|null|undefined} required
 */
export function normalizePermissionRequirement (required) {
  if (!required) return []
  if (Array.isArray(required)) {
    return required.map((n) => String(n).trim()).filter(Boolean)
  }
  const one = String(required).trim()
  return one ? [one] : []
}

/**
 * 是否拥有全部指定权限（AND）
 * @param {string[]} permissionNames
 * @param {string|string[]} required
 */
export function hasPermission (permissionNames, required) {
  const req = normalizePermissionRequirement(required)
  if (!req.length) return true
  const set = new Set(permissionNames || [])
  return req.every((name) => set.has(name))
}

/**
 * 是否拥有任一指定权限（OR，用于路由 meta.permission 数组）
 * @param {string[]} permissionNames
 * @param {string|string[]} required
 */
export function hasAnyPermission (permissionNames, required) {
  const req = normalizePermissionRequirement(required)
  if (!req.length) return true
  const set = new Set(permissionNames || [])
  return req.some((name) => set.has(name))
}

/**
 * 路由访问校验：meta.permission 为数组时表示任一即可
 */
export function checkRouteAccess (permissionNames, required) {
  const req = normalizePermissionRequirement(required)
  if (!req.length) return true
  if (Array.isArray(required)) {
    return hasAnyPermission(permissionNames, required)
  }
  return hasPermission(permissionNames, required)
}
